import { Link, Navigate } from 'react-router-dom';
import { MapPin, CheckCircle2, ChevronRight, Snowflake, Wrench, Flame, Shovel } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import FAQSection from '../components/FAQSection';
import CTABand from '../components/CTABand';
import { Reveal, Chapter } from '../components/motion';
import { SITE, IMAGES, CITIES, cityPath, localBusinessSchema, faqSchema, breadcrumbSchema } from '../data/site';
import { CITY_PAGES } from '../data/contentCities';

export default function CityPage({ citySlug }) {
  const slug = citySlug;
  const city = CITY_PAGES[slug];
  if (!city) return <Navigate to="/service-areas" replace />;

  const path = cityPath(slug);
  const h1 = `HVAC & Plumbing Services in ${city.name}, NC`;

  const sections = [
    {
      icon: Snowflake,
      h2: `HVAC & Air Conditioning Service in ${city.name}`,
      body: city.hvac,
      bullets: ['AC repair and fast diagnostics', 'AC installation & replacement', 'HVAC maintenance & tune-ups', 'Energy-efficient American Standard systems'],
      link: { label: 'Explore heating & air services', path: '/heating-air-conditioning' },
    },
    {
      icon: Wrench,
      h2: `Plumbing Services in ${city.name}`,
      body: city.plumbing,
      bullets: ['Residential plumbing repair', 'Commercial plumbing & upfits', 'New construction & custom homes', 'Water line & sewer line service'],
      link: { label: 'Explore plumbing services', path: '/plumbing' },
    },
    {
      icon: Flame,
      h2: `Heating Repair & Installation in ${city.name}`,
      body: `When winter settles over ${city.county}, ${city.name} families count on us for furnace repair, heat pump service, and heating replacement. Our technicians service all major brands, prioritize no-heat calls, and install high-efficiency American Standard heating systems sized correctly for your home.`,
      link: { label: 'Explore heating services', path: '/heating-repair-installation-stanfield-nc' },
    },
    {
      icon: Shovel,
      h2: `Excavation & Trenching Near ${city.name}`,
      body: `Underground water line or sewer line problems in ${city.name}? Our in-house excavation and trenching crew handles the digging while our plumbers handle the pipes — one company, one schedule, one accountable team from first shovel to final connection.`,
      link: { label: 'Explore excavation services', path: '/excavation-trenching' },
    },
  ];

  return (
    <>
      <SEO
        title={city.seoTitle}
        description={city.metaDescription}
        path={path}
        schemas={[
          localBusinessSchema,
          faqSchema(city.faqs),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Service Areas', path: '/service-areas' }, { name: `${city.name}, NC`, path }]),
        ]}
      />

      <PageHero
        eyebrow={`${city.county} · ${SITE.name}`}
        h1Lines={[`HVAC & Plumbing Services`, `in ${city.name}, NC`]}
        sub={city.intro[0]}
        image={IMAGES.heroTech}
        imageAlt={`HVAC and plumbing services in ${city.name}, NC by B.E. Holbrooks Co.`}
        crumbs={[{ name: 'Service Areas', path: '/service-areas' }, { name: `${city.name}, NC` }]}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <Chapter num="01" label={`Serving ${city.name}`} />
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{city.intro[1]}</p>
              <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{city.local}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <img
                src={IMAGES.techGauges}
                alt={`B.E. Holbrooks Co. technician providing HVAC and plumbing service in ${city.name}, NC`}
                loading="lazy"
                className="clip-frame w-full rounded-xl object-cover shadow-xl shadow-navy/15"
                data-testid="city-intro-image"
              />
            </Reveal>
          </div>

          <div className="mt-16 space-y-16">
            {sections.map((s, i) => (
              <Reveal key={s.h2}>
                <article className="grid gap-8 lg:grid-cols-12">
                  <div className="lg:col-span-4">
                    <div className="inline-flex rounded-lg bg-brand p-2.5 text-white"><s.icon className="h-5 w-5" /></div>
                    <h2 className="mt-3 font-display text-xl font-bold tracking-tight text-navy sm:text-2xl" data-testid={`city-section-h2-${i}`}>
                      {s.h2}
                    </h2>
                    <div className="mt-4 h-1 w-16 -skew-x-12 rounded bg-brand" />
                  </div>
                  <div className="lg:col-span-8">
                    <p className="text-base leading-relaxed text-slate-600 sm:text-lg">{s.body}</p>
                    {s.bullets && (
                      <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
                        {s.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2 text-sm font-medium text-slate-700">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-trustgreen" /> {b}
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link to={s.link.path} data-testid={`city-section-link-${i}`} className="mt-5 inline-flex items-center gap-1 font-display text-sm font-bold text-brand hover:text-brand-dark">
                      {s.link.label} <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-soft/50 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Chapter num="02" label="Nearby Communities" />
            <h2 className="mt-4 font-display text-2xl font-bold text-navy sm:text-3xl">Also Serving Communities Near {city.name}</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {CITIES.filter((c) => c.slug !== slug).map((c) => (
              <Link
                key={c.slug}
                to={cityPath(c.slug)}
                data-testid={`nearby-city-${c.slug}`}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-navy shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand hover:text-brand"
              >
                <MapPin className="h-4 w-4 text-brand" /> {c.name}, NC
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQSection faqs={city.faqs} heading={`HVAC & Plumbing FAQs — ${city.name}, NC`} chapterNum="03" idPrefix={`faq-${slug}`} />

      <CTABand
        heading={`Need HVAC or Plumbing Service in ${city.name}?`}
        text={`Call ${SITE.phone} today — ${city.name} is part of our core service area, and our technicians are in ${city.county} every day.`}
        testid={`cta-${slug}`}
      />
    </>
  );
}
